import React, { useEffect, useState } from "react";
import Shell from "@/components/layout/Shell";
import { PageHeader, PrimaryButton, GhostButton, EmptyState, Field, inputCls } from "@/components/common";
import { api } from "@/lib/api";
import { Plus, UserCog, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const AVATARS = [
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
  "https://images.unsplash.com/photo-1506863530036-1efeddceb993?crop=entropy&cs=srgb&fm=jpg&w=200&q=80",
];

export default function Equipo() {
  const [team, setTeam] = useState([]);
  const [clients, setClients] = useState([]);
  const [apps, setApps] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name:"", role:"", email:"", photo_url: AVATARS[0], assigned_client_ids: [] });

  const load = async () => {
    const [t,c,a] = await Promise.all([api.get("/team"), api.get("/clients"), api.get("/appointments")]);
    setTeam(t.data); setClients(c.data); setApps(a.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name:"", role:"", email:"", photo_url: AVATARS[Math.floor(Math.random()*4)], assigned_client_ids: [] }); setOpen(true); };
  const openEdit = (m) => { setEditing(m); setForm({ name:m.name, role:m.role, email:m.email||"", photo_url:m.photo_url||AVATARS[0], assigned_client_ids: m.assigned_client_ids||[] }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.role.trim()) { toast.error("Nombre y rol son obligatorios"); return; }
    try {
      if (editing) await api.put(`/team/${editing.member_id}`, form);
      else await api.post("/team", form);
      toast.success(editing ? "Actualizado" : "Miembro añadido");
      setOpen(false); load();
    } catch { toast.error("No se pudo guardar"); }
  };

  const remove = async (m) => {
    if (!window.confirm(`¿Eliminar a ${m.name}?`)) return;
    await api.delete(`/team/${m.member_id}`);
    toast.success("Eliminado"); load();
  };

  const toggleClient = (id) => {
    setForm(f => ({
      ...f,
      assigned_client_ids: f.assigned_client_ids.includes(id)
        ? f.assigned_client_ids.filter(x=>x!==id)
        : [...f.assigned_client_ids, id],
    }));
  };

  const clientMap = Object.fromEntries(clients.map(c=>[c.client_id, c.name]));

  return (
    <Shell>
      <PageHeader
        title="Equipo"
        subtitle={`${team.length} miembros activos`}
        action={<PrimaryButton data-testid="add-team-btn" onClick={openNew}><Plus className="w-4 h-4"/> Añadir miembro</PrimaryButton>}
      />

      {team.length === 0 ? (
        <EmptyState icon={UserCog} title="Sin miembros en el equipo"
          description="Añade community managers, diseñadores o consultores para asignarlos a clientes y citas."
          action={<PrimaryButton onClick={openNew}><Plus className="w-4 h-4"/> Añadir miembro</PrimaryButton>}
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {team.map(m => {
            const activeApps = apps.filter(a => a.team_member_id === m.member_id && a.status === "programada").length;
            return (
              <div key={m.member_id} data-testid={`team-card-${m.member_id}`} onClick={()=>openEdit(m)} className="bg-[#15171E] border border-[#262A33] hover:bg-[#1A1D24] rounded-2xl p-5 cursor-pointer transition-colors">
                <div className="flex items-start gap-3">
                  {m.photo_url ? (
                    <img src={m.photo_url} alt={m.name} className="w-14 h-14 rounded-full object-cover"/>
                  ) : (
                    <div className="w-14 h-14 rounded-full bg-[#262A33] flex items-center justify-center text-[#F5F5F7] font-bold">{m.name.charAt(0)}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#F5F5F7]" style={{ fontFamily: "Manrope, sans-serif" }}>{m.name}</div>
                    <div className="text-xs text-[#F97316] mt-0.5">{m.role}</div>
                    {m.email && <div className="text-xs text-[#9CA3AF] mt-0.5 truncate">{m.email}</div>}
                  </div>
                  <button onClick={(e)=>{ e.stopPropagation(); remove(m); }} className="p-1.5 rounded-lg text-[#6B7280] hover:text-red-400 hover:bg-red-500/10 transition-colors" aria-label="Eliminar">
                    <Trash2 className="w-4 h-4" strokeWidth={1.75}/>
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2 mt-4">
                  <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-3">
                    <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">Clientes</div>
                    <div className="text-lg font-bold text-[#F5F5F7]">{(m.assigned_client_ids||[]).length}</div>
                  </div>
                  <div className="bg-[#0B0D12] border border-[#262A33] rounded-xl p-3">
                    <div className="text-[10px] text-[#9CA3AF] uppercase tracking-wide">Citas activas</div>
                    <div className="text-lg font-bold text-[#F97316]">{activeApps}</div>
                  </div>
                </div>
                {(m.assigned_client_ids||[]).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {m.assigned_client_ids.slice(0,3).map(id => (
                      <span key={id} className="text-[10px] px-2 py-0.5 rounded-full bg-[#0B0D12] border border-[#262A33] text-[#9CA3AF]">{clientMap[id] || "?"}</span>
                    ))}
                    {m.assigned_client_ids.length > 3 && <span className="text-[10px] text-[#6B7280]">+{m.assigned_client_ids.length-3}</span>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#15171E] border-[#262A33] text-[#F5F5F7] max-w-lg">
          <DialogHeader><DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>{editing ? "Editar miembro" : "Nuevo miembro"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nombre *"><input data-testid="team-name-input" required className={inputCls} value={form.name} onChange={e=>setForm({...form, name: e.target.value})}/></Field>
              <Field label="Rol *"><input data-testid="team-role-input" required className={inputCls} placeholder="Community Manager" value={form.role} onChange={e=>setForm({...form, role: e.target.value})}/></Field>
            </div>
            <Field label="Email"><input type="email" className={inputCls} value={form.email} onChange={e=>setForm({...form, email: e.target.value})}/></Field>
            <Field label="Avatar">
              <div className="flex gap-2 mt-1">
                {AVATARS.map(url => (
                  <button key={url} type="button" onClick={()=>setForm({...form, photo_url: url})} className={`w-12 h-12 rounded-full overflow-hidden border-2 transition-colors ${form.photo_url===url ? "border-[#F97316]" : "border-transparent"}`}>
                    <img src={url} alt="avatar" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            </Field>
            {clients.length > 0 && (
              <Field label="Clientes asignados">
                <div className="max-h-32 overflow-auto bg-[#0B0D12] border border-[#262A33] rounded-xl p-2 space-y-1">
                  {clients.map(c => (
                    <label key={c.client_id} className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-[#1A1D24] cursor-pointer">
                      <input type="checkbox" checked={form.assigned_client_ids.includes(c.client_id)} onChange={()=>toggleClient(c.client_id)} className="accent-[#F97316]"/>
                      <span className="text-sm text-[#F5F5F7]">{c.name}</span>
                    </label>
                  ))}
                </div>
              </Field>
            )}
            <DialogFooter>
              <GhostButton type="button" onClick={()=>setOpen(false)}>Cancelar</GhostButton>
              <PrimaryButton data-testid="save-team-btn" type="submit">{editing ? "Guardar" : "Añadir"}</PrimaryButton>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
